import { useMemo, useState } from 'react';
import { Alert, Badge, Button, Form, ProgressBar, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { packItems, ceilingFor } from '../../helpers/tokenPlanner';
import { cableLabel } from '../../helpers/cableLabel';
import { clientColour } from '../../helpers/clientColour';
import TokenMapTable from './TokenMapTable';
import {
  slotProgress,
  slotProgressNote,
  slotProgressVariant
} from '../../helpers/slotProgress';

/**
 * Sending one product's cable to the orders that want it.
 *
 * The planner's decision, made in one place. Ten thousand metres came off a
 * machine; three orders want that cable; how much goes where is a commercial
 * call, and until now it was made for them — the packing chose which job paid
 * for which promise, and splitting one product across three orders meant
 * visiting three separate rows whose numbers moved as you went.
 *
 * Here the product is the subject. Every order that could take it is a row, its
 * delivery items are the quantities, and the cable left is one running total at
 * the top.
 */

const fmt = n => Math.round(Number(n) || 0).toLocaleString();


/**
 * How the due date should read.
 *
 * A planner deciding who gets scarce cable is deciding by date more than by
 * anything else, so the date carries its own urgency rather than sitting as
 * grey text like every other field.
 */
const dueTone = due => {
  if (!due) return { cls: 'text-body-tertiary', note: 'no date' };
  const days = dayjs(due).startOf('day').diff(dayjs().startOf('day'), 'day');
  if (days < 0) return { cls: 'text-danger fw-bold', note: `${-days}d late` };
  if (days === 0) return { cls: 'text-danger fw-bold', note: 'today' };
  if (days <= 7) return { cls: 'text-warning fw-bold', note: `in ${days}d` };
  return { cls: 'fw-semibold', note: `in ${days}d` };
};

const describeItem = it =>
  `${fmt(it.lengthPerUnit)} m ${
    String(it.kind).toUpperCase() === 'COIL'
      ? 'coil'
      : [it.drumMaterial, it.drumDiameter ? `Ø${it.drumDiameter}` : '']
          .filter(Boolean)
          .join(' ') || 'drum'
  }`;

const ProductAllocationModal = ({ product, busy, onAllocate, onRelease }) => {
  const [chosen, setChosen] = useState({});
  const [error, setError] = useState(null);
  const [releasing, setReleasing] = useState(null);

  const release = async (promise, key) => {
    setReleasing(key);
    setError(null);
    try {
      await onRelease(promise);
    } catch (err) {
      setError(err?.message || 'Could not take it back');
    } finally {
      setReleasing(null);
    }
  };

  // One flat list of everything this product could be turned into, across every
  // order — the packing works on lengths and does not care whose they are.
  const slots = useMemo(() => {
    const out = [];
    for (const c of product?.candidates || []) {
      for (const it of c.items || []) {
        if (it.unitsOpen <= 0) continue;
        out.push({
          key: `${c.orderId}::${c.lineId}::${it.itemId}`,
          orderId: c.orderId,
          orderCode: c.orderCode,
          client: c.client,
          dueDate: c.dueDate,
          createdAt: c.createdAt,
          lineId: c.lineId,
          label: cableLabel(c.product, c.productKey),
          itemId: it.itemId,
          kind: it.kind,
          lengthPerUnit: it.lengthPerUnit,
          cap: it.unitsOpen,
          description: describeItem(it),
          // What the order already has, so "30 × 100 m coil" stops reading as
          // a fresh slot when it is three-quarters built.
          item: it
        });
      }
    }
    // Soonest due first. Scarce cable goes to whoever needs it first, so the
    // list should already be in the order the decision gets made in.
    return out.sort((a, b) => {
      const av = a.dueDate ? dayjs(a.dueDate).valueOf() : Infinity;
      const bv = b.dueDate ? dayjs(b.dueDate).valueOf() : Infinity;
      return av - bv;
    });
  }, [product]);

  const preview = useMemo(() => {
    const demands = slots.map(s => ({
      itemId: s.key,
      lengthPerUnit: s.lengthPerUnit,
      units: Math.min(Number(chosen[s.key]) || 0, s.cap)
    }));
    const { results, leftover } = packItems({
      pieces: product?.pieces || [],
      demands
    });
    const byKey = new Map(results.map(r => [String(r.itemId), r]));

    // What each slot could still have, given everything else already chosen.
    const room = new Map();
    for (const s of slots) {
      const others = demands.filter(d => d.itemId !== s.key);
      const { leftover: spare } = packItems({
        pieces: product?.pieces || [],
        demands: others
      });
      room.set(s.key, Math.min(ceilingFor(spare, s.lengthPerUnit, s.key), s.cap));
    }

    return {
      byKey,
      room,
      used: results.reduce((t, r) => t + r.metersUsed, 0),
      left: leftover.reduce((t, p) => t + p.free, 0),
      short: results.filter(r => r.unitsShort > 0)
    };
  }, [slots, chosen, product]);

  const setUnits = (key, n, cap) =>
    setChosen(prev => ({
      ...prev,
      [key]: Math.max(0, Math.min(Math.floor(Number(n) || 0), cap))
    }));

  const total = Object.values(chosen).reduce((s, n) => s + (Number(n) || 0), 0);

  const run = async () => {
    setError(null);
    const picks = [];
    for (const s of slots) {
      const r = preview.byKey.get(s.key);
      if (!r?.unitsFeasible) continue;
      picks.push({
        orderId: s.orderId,
        lineId: s.lineId,
        itemId: s.itemId,
        lengthPerUnit: s.lengthPerUnit,
        units: r.unitsFeasible,
        candidates: r.candidates
      });
    }
    if (!picks.length) return;
    try {
      await onAllocate(picks);
      setChosen({});
    } catch (err) {
      setError(err?.message || 'Could not allocate');
    }
  };

  if (!product) return null;

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap gap-3 mb-3">
        <div>
          <div className="fs-9 text-body-tertiary">This product makes</div>
          <div className="fs-7 fw-bold">{fmt(product.plannedLength)} m</div>
        </div>
        <div>
          <div className="fs-9 text-body-tertiary">Already promised</div>
          <div className="fs-7 fw-bold text-info">
            {fmt(product.promisedMeters)} m
          </div>
        </div>
        <div>
          <div className="fs-9 text-body-tertiary">Free to give</div>
          <div className="fs-7 fw-bold text-success">
            {fmt(product.freeMeters)} m
          </div>
        </div>
        <div className="ms-auto text-end">
          <div className="fs-9 text-body-tertiary">This plan</div>
          <div className="fs-8 fw-bold">
            {fmt(preview.used)} m out · {fmt(preview.left)} m stays free
          </div>
        </div>
      </div>

      {/* Already promised, with the way back out. A list you cannot act on is
          just a number, and this screen is where the promise was made — the
          undo belongs beside it. */}
      {product.promised?.length > 0 && (
        <div className="mb-3">
          <h6 className="fs-9 text-1100 mb-2">Already promised</h6>
          {product.promised.map(q => {
            const key = `${q.orderId}::${q.lineId}::${q.itemId}`;
            return (
              <div
                key={key}
                className="d-flex align-items-center gap-2 mb-1 flex-wrap"
              >
                <Badge bg={clientColour(q.client)} variant="phoenix" className="fs-10">
                  {String(q.client || '—').toUpperCase()}
                </Badge>
                <span className="fs-10 fw-semibold">{q.orderCode}</span>
                <span className="fs-10 text-body-tertiary">
                  {fmt(q.units)} × {fmt(q.lengthPerUnit)} m{' '}
                  {String(q.kind).toLowerCase()} = {fmt(q.meters)} m
                </span>
                <Button
                  size="sm"
                  variant="phoenix-secondary"
                  disabled={releasing === key || !!busy}
                  onClick={() => release(q, key)}
                >
                  {releasing === key ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    'Take back'
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <Alert variant="danger" className="py-2 fs-9">
          {error}
        </Alert>
      )}

      {!slots.length ? (
        <p className="text-body-tertiary fs-9 mb-0">
          No order is waiting for this product. Its cable stays in stock.
        </p>
      ) : (
        <div className="table-responsive">
          <Table size="sm" className="fs-9 mb-0">
            <thead>
              <tr>
                <th>Order</th>
                <th>Wants</th>
                <th style={{ width: 155 }}>Send</th>
                <th className="text-end">Room</th>
                <th className="text-end">Metres</th>
              </tr>
            </thead>
            <tbody>
              {slots.map(s => {
                const r = preview.byKey.get(s.key);
                const room = preview.room.get(s.key) || 0;
                const now = Math.min(Number(chosen[s.key]) || 0, s.cap);
                return (
                  <tr key={s.key}>
                    <td>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <Badge
                          bg={clientColour(s.client)}
                          variant="phoenix"
                          className="fs-10"
                        >
                          {String(s.client || '—').toUpperCase()}
                        </Badge>
                        <span className="fw-semibold">{s.orderCode}</span>
                      </div>
                      <div className="fs-10">
                        <span className="text-body-tertiary">
                          placed {s.createdAt || '—'}
                        </span>
                        <span className="text-body-tertiary"> · due </span>
                        <span className={dueTone(s.dueDate).cls}>
                          {s.dueDate || '—'}
                          {s.dueDate ? ` (${dueTone(s.dueDate).note})` : ''}
                        </span>
                      </div>
                    </td>
                    <td>
                      <Badge
                        bg={
                          String(s.kind).toUpperCase() === 'COIL'
                            ? 'success'
                            : 'info'
                        }
                        variant="phoenix"
                        className="me-2"
                      >
                        {s.kind}
                      </Badge>
                      {fmt(s.cap)} × {s.description}
                      {(() => {
                        const note = slotProgressNote(s.item);
                        if (!note) return null;
                        const p = slotProgress(s.item);
                        return (
                          <div className="mt-1" style={{ maxWidth: 220 }}>
                            <ProgressBar
                              now={p.pct}
                              style={{ height: 5 }}
                              variant={slotProgressVariant(s.item)}
                            />
                            <div className="fs-10 text-body-tertiary mt-1">{note}</div>
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        <Button
                          size="sm"
                          variant="phoenix-danger"
                          disabled={now <= 0}
                          onClick={() => setUnits(s.key, now - 1, s.cap)}
                        >
                          <FontAwesomeIcon icon={faMinus} />
                        </Button>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={0}
                          max={room}
                          value={now}
                          onChange={e => setUnits(s.key, e.target.value, room)}
                          style={{ width: 92 }}
                          className="text-center fw-bold input-spin-none"
                        />
                        <Button
                          size="sm"
                          variant="phoenix-success"
                          disabled={room <= now}
                          onClick={() => setUnits(s.key, now + 1, room)}
                        >
                          <FontAwesomeIcon icon={faPlus} />
                        </Button>
                      </div>
                      {r?.unitsShort > 0 && (
                        <div className="text-danger fs-10 mt-1">
                          only {r.unitsFeasible} can be cut
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      <span className={room > 0 ? 'fw-semibold' : 'text-body-tertiary'}>
                        {fmt(room)}
                      </span>
                      <div className="text-body-tertiary fs-10">
                        order wants {fmt(s.cap)}
                      </div>
                    </td>
                    <td className="text-end fw-semibold">
                      {fmt((r?.unitsFeasible || 0) * s.lengthPerUnit)} m
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* The structure of the product, under the choice it decides.
          "Can I promise a 3,000 m drum?" is answered by what the drums on the
          floor actually are — how the length got broken up, what is finished,
          what is planned onward and what is still sitting. Putting it anywhere
          else means leaving the decision to go and look. */}
      {product.chains?.length > 0 && (
        <div className="mt-4 pt-3 border-top">
          <TokenMapTable job={product} />
        </div>
      )}

      {preview.short.length > 0 && (
        <Alert variant="warning" className="py-2 fs-9 mt-2 mb-0">
          Some of what you asked for cannot be cut from this product — lower the
          numbers, or send the rest from another launch.
        </Alert>
      )}

      {slots.length > 0 && (
        <div className="d-flex align-items-center gap-2 mt-3">
          <Button
            variant="primary"
            size="sm"
            disabled={!total || !!busy || preview.short.length > 0}
            onClick={run}
          >
            {busy ? <Spinner animation="border" size="sm" /> : 'Send to these orders'}
          </Button>
          <Button
            variant="phoenix-secondary"
            size="sm"
            disabled={!total || !!busy}
            onClick={() => setChosen({})}
          >
            Clear
          </Button>
          <span className="text-body-tertiary fs-10">
            {fmt(preview.left)} m stays in stock
          </span>
        </div>
      )}
    </div>
  );
};

export default ProductAllocationModal;
